import React, { useState } from 'react';
import { collection, query, getDocs, where, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';

interface UserData {
  nombre: string;
  correo: string;
  tipo: 'Afiliado' | 'Asociado';
  agencia: string;
  telefono?: string;
  hasFirebaseAuth?: boolean;
  firebaseUid?: string;
  docId?: string;
}

interface CSVRow {
  Nombre: string;
  Tipo: string;
  Telefono: string;
  Correo: string;
  Agencia: string;
  'Sitio web': string;
}

const FixFirebaseAccounts: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [missingUsers, setMissingUsers] = useState<UserData[]>([]);
  const [existingUsers, setExistingUsers] = useState<UserData[]>([]);
  const [creationResults, setCreationResults] = useState<any[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Parsear CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const data: CSVRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim());
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          data.push(row);
        }
      }

      setCsvData(data);
      console.log(`CSV cargado: ${data.length} registros`);
    };
    reader.readAsText(file);
  };

  // Analizar usuarios faltantes
  const analyzeUsers = async () => {
    if (csvData.length === 0) {
      alert('Por favor carga el archivo CSV primero');
      return;
    }

    setAnalyzing(true);
    const missing: UserData[] = [];
    const existing: UserData[] = [];

    try {
      for (const row of csvData) {
        const email = row.Correo?.toLowerCase().trim();
        if (!email || !email.includes('@')) continue;

        // Buscar en la colección correspondiente
        const collectionName = row.Tipo === 'Afiliado' ? 'asesor' : 'asociado';
        const q = query(collection(db, collectionName), where('correo', '==', email));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          // No existe en Firestore
          missing.push({
            nombre: row.Nombre,
            correo: email,
            tipo: row.Tipo as 'Afiliado' | 'Asociado',
            agencia: row.Agencia,
            telefono: row.Telefono,
            hasFirebaseAuth: false
          });
        } else {
          // Existe en Firestore, verificar firebase_uid
          const doc = snapshot.docs[0];
          const data = doc.data();

          if (!data.firebase_uid) {
            missing.push({
              nombre: row.Nombre,
              correo: email,
              tipo: row.Tipo as 'Afiliado' | 'Asociado',
              agencia: row.Agencia,
              telefono: row.Telefono,
              hasFirebaseAuth: false,
              docId: doc.id
            });
          } else {
            existing.push({
              nombre: row.Nombre,
              correo: email,
              tipo: row.Tipo as 'Afiliado' | 'Asociado',
              agencia: row.Agencia,
              hasFirebaseAuth: true,
              firebaseUid: data.firebase_uid
            });
          }
        }
      }

      setMissingUsers(missing);
      setExistingUsers(existing);
      console.log(`Análisis completado: ${missing.length} sin Firebase, ${existing.length} con Firebase`);

    } catch (error) {
      console.error('Error analizando usuarios:', error);
      alert('Error al analizar usuarios');
    } finally {
      setAnalyzing(false);
    }
  };

  // Generar contraseña aleatoria
  const generatePassword = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Crear cuentas Firebase faltantes
  const createMissingAccounts = async () => {
    if (missingUsers.length === 0) {
      alert('No hay usuarios pendientes');
      return;
    }

    if (!window.confirm(`¿Crear ${missingUsers.length} cuentas Firebase?`)) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: missingUsers.length });
    const results: any[] = [];

    for (let i = 0; i < missingUsers.length; i++) {
      const user = missingUsers[i];
      setProgress({ current: i + 1, total: missingUsers.length });

      try {
        const password = generatePassword();

        // Crear cuenta en Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          user.correo,
          password
        );

        // Si el usuario existe en Firestore, actualizar con firebase_uid
        if (user.docId) {
          const collectionName = user.tipo === 'Afiliado' ? 'asesor' : 'asociado';
          const docRef = collection(db, collectionName);
          const q = query(docRef, where('correo', '==', user.correo));
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            await updateDoc(snapshot.docs[0].ref, {
              firebase_uid: userCredential.user.uid
            });
          }
        }

        results.push({
          success: true,
          nombre: user.nombre,
          email: user.correo,
          password: password,
          uid: userCredential.user.uid,
          mensaje: 'Cuenta creada exitosamente'
        });

      } catch (error: any) {
        let mensaje = error.message;
        if (error.code === 'auth/email-already-in-use') {
          mensaje = 'Email ya existe en Firebase Auth';
        }

        results.push({
          success: false,
          nombre: user.nombre,
          email: user.correo,
          error: mensaje
        });
      }
    }

    setCreationResults(results);
    setLoading(false);

    // Descargar resultados como JSON
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (successCount > 0) {
      const dataStr = JSON.stringify(results.filter(r => r.success), null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `firebase_accounts_${new Date().toISOString().slice(0,10)}.json`;

      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }

    alert(`Proceso completado:\n✅ Creadas: ${successCount}\n❌ Fallidas: ${failCount}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            🔧 Crear Cuentas Firebase Faltantes
          </h1>

          {/* Paso 1: Cargar CSV */}
          <div className="mb-8 p-6 bg-blue-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Paso 1: Cargar archivo CSV</h2>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="mb-4"
            />
            {csvData.length > 0 && (
              <p className="text-green-600">
                ✅ CSV cargado: {csvData.length} registros
              </p>
            )}
          </div>

          {/* Paso 2: Analizar */}
          {csvData.length > 0 && (
            <div className="mb-8 p-6 bg-yellow-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Paso 2: Analizar usuarios</h2>
              <button
                onClick={analyzeUsers}
                disabled={analyzing}
                className="px-6 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
              >
                {analyzing ? 'Analizando...' : 'Analizar Usuarios'}
              </button>

              {(missingUsers.length > 0 || existingUsers.length > 0) && (
                <div className="mt-4 space-y-2">
                  <p className="text-green-600">
                    ✅ Con Firebase Auth: {existingUsers.length}
                  </p>
                  <p className="text-red-600">
                    ❌ Sin Firebase Auth: {missingUsers.length}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Paso 3: Crear cuentas */}
          {missingUsers.length > 0 && (
            <div className="mb-8 p-6 bg-green-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">
                Paso 3: Crear {missingUsers.length} cuentas faltantes
              </h2>

              <button
                onClick={createMissingAccounts}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? `Creando... (${progress.current}/${progress.total})` : 'Crear Cuentas'}
              </button>

              {/* Lista de usuarios sin cuenta */}
              <div className="mt-6 max-h-96 overflow-y-auto">
                <h3 className="font-semibold mb-2">Usuarios sin Firebase Auth:</h3>
                <div className="space-y-1 text-sm">
                  {missingUsers.map((user, index) => (
                    <div key={index} className="p-2 bg-white rounded border">
                      {index + 1}. {user.nombre} ({user.tipo}) - {user.correo} - {user.agencia}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Resultados */}
          {creationResults.length > 0 && (
            <div className="p-6 bg-gray-50 rounded-lg">
              <h2 className="text-lg font-semibold mb-4">Resultados:</h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {creationResults.map((result, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded ${result.success ? 'bg-green-100' : 'bg-red-100'}`}
                  >
                    {result.success ? '✅' : '❌'} {result.nombre} - {result.email}
                    {result.success && <span className="text-xs ml-2">UID: {result.uid}</span>}
                    {!result.success && <span className="text-xs ml-2">Error: {result.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FixFirebaseAccounts;